import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Field, Formik } from 'formik';
import { Heading } from '../../ExperianStyles';
import FormikInput from '../../../CommonComponents/FormikInput';
import Modal from '../../../CommonComponents/Modal';
import { PrimaryButton, SecondaryButton } from '../../../CommonComponents/Button';
import { numberMask, ssnMask } from '../../../CommonComponents/MaskedInput';
import FormikMaskedInput from '../../../CommonComponents/FormikMaskedInput';
import FormikError from '../../../CommonComponents/FormikError';
import FormikDateInput from '../../../CommonComponents/FormikDateInput';
import { Alert } from '../../../CommonComponents/Alert/Alert';
import Spinner from '../../../CommonComponents/Spinner';
import { enroll } from '../../ExperianActions';
import { enrollErrorSelector, isCancellingSubscriptionSelector } from '../../ExperianSelector';
import validationSchema from './EnrollFormValidation';
import { Footer, Label, LabelText, StyledForm } from './EnrollFormStyles';
import FormikSelect from '../../../CommonComponents/FormikSelect';
import { INITIAL_VALUES, STATES } from './EnrollFormConstants';
import ExperianContext from '../../ExperianContext';

const EnrollForm = ({ isOpen, onCancel }) => {
  const isCancellingSubscription = useSelector(isCancellingSubscriptionSelector);
  const error = useSelector(enrollErrorSelector);

  const { clientId } = useContext(ExperianContext);

  const dispatch = useDispatch();

  return (
    <Modal isOpen={isOpen} showCloseModalCross onRequestClose={onCancel}>
      <Formik
        initialValues={INITIAL_VALUES}
        validationSchema={validationSchema}
        onSubmit={async (values, actions) => {
          await dispatch(enroll(clientId, values));
          actions.setSubmitting(false);
        }}
      >
        {({ errors, touched, setFieldValue, isSubmitting }) => (
          <StyledForm>
            {isSubmitting && <Spinner />}
            {error && <Alert message={error} />}
            <Heading>Enroll in Online Identity and Financial Account Monitoring Services</Heading>
            <Label>
              <LabelText>First Name</LabelText>
              <Field type="text" name="firstName" component={FormikInput} size="small" />
              {errors.firstName && touched.firstName && <FormikError name="firstName" />}
            </Label>
            <Label>
              <LabelText>Last Name</LabelText>
              <Field type="text" name="lastName" component={FormikInput} size="small" />
              {errors.lastName && touched.lastName && <FormikError name="lastName" />}
            </Label>
            <Label>
              <LabelText>Email</LabelText>
              <Field type="text" name="email" component={FormikInput} size="small" />
              {errors.email && touched.email && <FormikError name="email" />}
            </Label>
            <Label>
              <LabelText>Date of Birth</LabelText>
              <Field
                name="dob"
                component={FormikDateInput}
                onChange={day => setFieldValue('dob', day)}
                size="small"
              />
              {errors.dob && touched.dob && <FormikError name="dob" />}
            </Label>
            <Label>
              <LabelText>Social Security Number</LabelText>
              <Field
                type="text"
                name="ssn"
                component={FormikMaskedInput}
                mask={ssnMask}
                guide={false}
                size="small"
              />
              {errors.ssn && touched.ssn && <FormikError name="ssn" />}
            </Label>
            <Label>
              <LabelText>Address</LabelText>
              <Field type="text" name="address1" component={FormikInput} size="small" />
              {errors.address1 && touched.address1 && <FormikError name="address1" />}
            </Label>
            <Label as="div">
              <Field
                type="text"
                name="address2"
                component={FormikInput}
                size="small"
                aria-label="Address 2"
              />
              {errors.address2 && touched.address2 && <FormikError name="address2" />}
            </Label>
            <Label>
              <LabelText>City</LabelText>
              <Field type="text" name="city" component={FormikInput} size="small" />
              {errors.city && touched.city && <FormikError name="city" />}
            </Label>
            <Label>
              <LabelText>State</LabelText>
              <Field
                type="text"
                name="state"
                component={FormikSelect}
                options={STATES}
                size="small"
              />
              {errors.state && touched.state && <FormikError name="state" />}
            </Label>
            <Label>
              <LabelText>Zip</LabelText>
              <Field
                type="text"
                name="zip"
                component={FormikMaskedInput}
                mask={numberMask}
                size="small"
              />
              {errors.zip && touched.zip && <FormikError name="zip" />}
            </Label>
            <Footer>
              <SecondaryButton onClick={onCancel} disabled={isCancellingSubscription}>
                Cancel
              </SecondaryButton>
              <PrimaryButton type="submit" disabled={isSubmitting}>
                Submit
              </PrimaryButton>
            </Footer>
          </StyledForm>
        )}
      </Formik>
    </Modal>
  );
};

EnrollForm.defaultProps = {
  onCancel: null,
};

EnrollForm.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onCancel: PropTypes.func,
};

export default EnrollForm;
